const chapter = {
  id: 'thermodynamics-chem',
  slug: 'thermodynamics-chem',
  subject: 'chemistry',
  subjectLabel: 'Chemistry',
  exam: 'JEE Main + NEET',
  title: {
    english: 'Thermodynamics',
        hindi: 'ऊष्मागतिकी',
  },
  estReadMinutes: 75,
  difficulty: {
    english: 'Hard',
        hindi: 'कठिन',
  },
  weightage: {
    english: '2–3 questions in JEE Main / NEET',
        hindi: 'जेईई मेन/एनईईटी में 2-3 प्रश्न',
  },
  syllabusTags: ['Physical Chemistry', 'JEE-Main', 'NEET', 'Class-11'],
  sections: [
    {
      id: 'basic-concepts',
      title: {
        english: 'System, Surroundings, State Functions',
        hindi: 'निकाय, परिवेश, अवस्था फलन',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**System**: portion under study. **Surroundings**: rest of universe. Boundary may be real or imaginary.',
        hindi: '**प्रणाली**: अध्ययनाधीन भाग। **परिवेश**: शेष ब्रह्मांड। सीमा वास्तविक या काल्पनिक हो सकती है।',
            },
            {
              english: 'Types: **open** (mass + energy exchange), **closed** (only energy), **isolated** (neither).',
        hindi: 'प्रकार: **खुला** (द्रव्यमान + ऊर्जा विनिमय), **बंद** (केवल ऊर्जा), **पृथक** (कोई नहीं)। **स्टेट फ़ंक्शन**: केवल स्टेट पर निर्भर करता है, पथ पर नहीं। उदाहरण: . **पथ फ़ंक्शन**: मार्ग पर निर्भर करता है। उदाहरण: ताप , कार्य ।',
            },
            {
              english: '**State function**: depends only on state, not path. Examples: $U, H, S, G, P, V, T$.',
        hindi: '**राज्य कार्य**: केवल राज्य पर निर्भर करता है, पथ पर नहीं। उदाहरण: $U, H, S, G, P, V, T$.',
            },
            {
              english: '**Path function**: depends on the route. Examples: heat $q$, work $w$.',
        hindi: '**पथ फ़ंक्शन**: मार्ग पर निर्भर करता है। उदाहरण: ताप $q$, कार्य $w$।',
            },
            {
              english: 'Conventions (IUPAC, modern NCERT): $q > 0$ heat absorbed by system; $w > 0$ work done **on** the system. So $\\Delta U = q + w$.',
        hindi: 'कन्वेंशन (IUPAC, आधुनिक NCERT): $q > 0$ सिस्टम द्वारा अवशोषित ऊष्मा; $w > 0$ सिस्टम पर **कार्य किया गया। तो $\\Delta U = q + w$।',
            },
          ],
        },
        {
          type: 'list',
          items: [
            {
              english: 'Processes: **isothermal** ($\\Delta T = 0$), **adiabatic** ($q = 0$), **isobaric** ($\\Delta P = 0$), **isochoric** ($\\Delta V = 0$), **cyclic** ($\\Delta$ all state functions $= 0$).',
        hindi: 'प्रक्रियाएं: **आइसोथर्मल** ($\\Delta T = 0$), **एडियाबेटिक** ($q = 0$), **आइसोबैरिक** ($\\Delta P = 0$), **आइसोकोरिक** ($\\Delta V = 0$), **चक्रीय** ($\\Delta$ सभी राज्य कार्य $= 0$).',
            },
            {
              english: '**Reversible**: infinitely slow, equilibrium at every step; maximum work obtainable from system. **Irreversible**: real processes; less work.',
        hindi: '**प्रतिवर्ती**: असीम रूप से धीमा, हर कदम पर संतुलन; सिस्टम से प्राप्त अधिकतम कार्य। **अपरिवर्तनीय**: वास्तविक प्रक्रियाएं; कम काम.',
            },
          ],
        },
      ],
    },
    {
      id: 'first-law',
      title: {
        english: 'First Law & PV Work',
        hindi: 'प्रथम नियम और PV कार्य',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'First law of thermodynamics',
        hindi: 'ऊष्मागतिकी का पहला नियम',
          },
          statement: {
            english: '$\\Delta U = q + w$. Energy of an isolated system is constant.',
        hindi: '$\\Delta U = q + w$। किसी पृथक निकाय की ऊर्जा स्थिर होती है।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Expansion / compression work** against external pressure: $w = -P_{\\text{ext}}\\,\\Delta V$. (Positive when gas is compressed.)',
        hindi: '**बाहरी दबाव के विरुद्ध विस्तार/संपीड़न कार्य**: $w = -P_{\\text{ext}}\\,\\Delta V$। (गैस संपीड़ित होने पर सकारात्मक।)',
            },
            {
              english: '**Free expansion** into vacuum ($P_{\\text{ext}} = 0$): $w = 0$. If also adiabatic ⇒ $\\Delta U = 0$, $\\Delta T = 0$ for ideal gas.',
        hindi: '**नि:शुल्क विस्तार** निर्वात में ($P_{\\text{ext}} = 0$): $w = 0$। यदि रुद्धोष्म भी है ⇒ आदर्श गैस के लिए $\\Delta U = 0$, $\\Delta T = 0$।',
            },
            {
              english: '**Reversible isothermal** (ideal gas): $w_{rev} = -nRT \\ln(V_2/V_1) = -nRT\\ln(P_1/P_2)$. $\\Delta U = 0$, $q = -w$.',
        hindi: '**प्रतिवर्ती इज़ोटेर्मल** (आदर्श गैस): $w_{rev} = -nRT \\ln(V_2/V_1) = -nRT\\ln(P_1/P_2)$। $\\Delta U = 0$, $q = -w$.',
            },
            {
              english: '**Reversible adiabatic** (ideal gas): $PV^\\gamma$ = const; $TV^{\\gamma-1}$ = const; $T P^{(1-\\gamma)/\\gamma}$ = const. $w = nC_V \\Delta T$, $q = 0$.',
        hindi: '**प्रतिवर्ती रुद्धोष्म** (आदर्श गैस): $PV^\\gamma$ = स्थिरांक; $TV^{\\gamma-1}$ = स्थिरांक; $T P^{(1-\\gamma)/\\gamma}$ = स्थिरांक। $w = nC_V \\Delta T$, $q = 0$।',
            },
          ],
        },
        {
          type: 'definition',
          term: {
            english: 'Enthalpy $H$',
        hindi: 'एन्थैल्पी $H$',
          },
          body: {
            english: '$H = U + PV$. At constant $P$: $\\Delta H = q_p$. For ideal gas: $\\Delta H = \\Delta U + \\Delta n_g RT$ where $\\Delta n_g$ = change in moles of gases.',
        hindi: '$H = U + PV$. निरंतर $P$: $\\Delta H = q_p$ पर। आदर्श गैस के लिए: $\\Delta H = \\Delta U + \\Delta n_g RT$ जहां $\\Delta n_g$ = गैसों के मोल में परिवर्तन।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Heat capacity at constant volume: $C_V = (\\partial U/\\partial T)_V$. At constant pressure: $C_P = (\\partial H/\\partial T)_P$.',
        hindi: 'स्थिर आयतन पर ऊष्मा क्षमता: $C_V = (\\partial U/\\partial T)_V$। निरंतर दबाव पर: $C_P = (\\partial H/\\partial T)_P$.',
            },
            {
              english: 'For ideal gas: $C_P - C_V = R$. **Mayer relation**.',
        hindi: 'आदर्श गैस के लिए: $C_P - C_V = R$। **मेयर संबंध**.',
            },
            {
              english: 'For monatomic: $C_V = 3R/2$, $\\gamma = 5/3$. Diatomic: $C_V = 5R/2$, $\\gamma = 7/5$. Polyatomic (linear): $C_V = 6R/2$, $\\gamma = 8/6$.',
        hindi: 'एकपरमाण्विक के लिए: $C_V = 3R/2$, $\\gamma = 5/3$। डायटोमिक: $C_V = 5R/2$, $\\gamma = 7/5$। बहुपरमाणुक (रैखिक): $C_V = 6R/2$, $\\gamma = 8/6$।',
            },
            {
              english: '$q = nC \\Delta T$ (constant capacity).',
        hindi: '$q = nC \\Delta T$ (निरंतर क्षमता)।',
            },
          ],
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: '2 mol ideal gas expands isothermally and reversibly from 5 L to 50 L at 300 K. Find $w, q, \\Delta U, \\Delta H$.',
        hindi: '2 मोल आदर्श गैस 300 K पर 5 L से 50 L तक समतापीय और विपरीत रूप से फैलती है। $w, q, \\Delta U, \\Delta H$ खोजें।',
          },
          solution: {
            english: `$\\Delta U = 0$ (isothermal). $w = -nRT\\ln(V_2/V_1) = -2(8.314)(300)\\ln 10 = -11486$ J.
$q = -w = +11486$ J. $\\Delta H = \\Delta U + \\Delta(PV) = 0$.`,
            hindi: '$\\Delta U = 0$ (आइसोथर्मल)। $w = -nRT\\ln(V_2/V_1) = -2(8.314)(300)\\ln 10 = -11486$ जे. $q = -w = +11486$ जे. $\\Delta H = \\Delta U + \\Delta(PV) = 0$.',
          },
        },
      ],
    },
    {
      id: 'thermochemistry',
      title: {
        english: 'Thermochemistry — Reaction Enthalpies & Hess\'s Law',
        hindi: 'थर्मोकैमिस्ट्री - रिएक्शन एन्थैल्पीज़ और हेस का नियम',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Standard state**: pure substance at 1 bar, specified $T$ (usually 298.15 K). Symbol: $\\Delta H^\\circ$.',
        hindi: '**मानक अवस्था**: 1 बार पर शुद्ध पदार्थ, निर्दिष्ट $T$ (आमतौर पर 298.15 K)। प्रतीक: $\\Delta H^\\circ$.',
            },
            {
              english: '**Enthalpy of formation $\\Delta_f H^\\circ$**: for forming 1 mole of compound from elements in their standard states. By convention, $\\Delta_f H^\\circ$ of element in std state = 0.',
        hindi: '**गठन की एन्थैल्पी $\\Delta_f H^\\circ$**: तत्वों से उनकी मानक अवस्था में 1 मोल यौगिक बनाने के लिए। परंपरा के अनुसार, कक्षा में तत्व का $\\Delta_f H^\\circ$ state = 0।',
            },
            {
              english: '**Enthalpy of combustion $\\Delta_c H^\\circ$**: 1 mole burnt completely in O₂. Always negative.',
        hindi: '**दहन की एन्थैल्पी $\\Delta_c H^\\circ$**: 1 मोल O₂ में पूरी तरह से जल गया। हमेशा नकारात्मक.',
            },
            {
              english: '**Enthalpy of neutralisation**: H⁺ + OH⁻ → H₂O. For strong acid + strong base $\\approx -57.1$ kJ/mol.',
        hindi: '**निष्क्रियता की एन्थैल्पी**: H⁺ + OH⁻ → H₂O. प्रबल अम्ल + प्रबल क्षार के लिए $\\approx -57.1$ kJ/mol।',
            },
            {
              english: '**Bond enthalpy**: energy to break 1 mol of bonds in gas phase. $\\Delta_r H \\approx \\sum$BE(reactants) $-\\sum$BE(products).',
        hindi: '**बॉन्ड एन्थैल्पी**: गैस चरण में 1 मोल बॉन्ड को तोड़ने की ऊर्जा। $\\Delta_r H \\approx \\sum$BE(अभिकारक) $-\\sum$BE(उत्पाद)।',
            },
            {
              english: '**Lattice enthalpy**, **hydration enthalpy**, **enthalpy of solution**, **ionisation enthalpy**, **electron-gain enthalpy**.',
        hindi: '**Common Trapक एन्थैल्पी**, **जलयोजन एन्थैल्पी**, **समाधान की एन्थैल्पी**, **आयनीकरण एन्थैल्पी**, **इलेक्ट्रॉन-लाभ एन्थैल्पी**।',
            },
          ],
        },
        {
          type: 'theorem',
          name: {
            english: 'Hess\'s law of constant heat summation',
        hindi: 'हेस का निरंतर ऊष्मा योग का नियम',
          },
          statement: {
            english: 'Total enthalpy change for a reaction is independent of the path taken. Hence reactions can be added/subtracted/multiplied; their $\\Delta H$ values follow accordingly.',
        hindi: 'किसी प्रतिक्रिया के लिए कुल एन्थैल्पी परिवर्तन लिए गए पथ से स्वतंत्र होता है। इसलिए प्रतिक्रियाएं added/subtracted/गुणा हो सकती हैं; उनके $\\Delta H$ मान तदनुसार अनुसरण करते हैं।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'From formation enthalpies',
        hindi: 'गठन एन्थैल्पी से',
          },
          statement: {
            english: '$\\Delta_r H^\\circ = \\sum \\nu_i \\Delta_f H^\\circ(\\text{products}) - \\sum \\nu_j \\Delta_f H^\\circ(\\text{reactants})$.',
        hindi: '$\\Delta_r H^\\circ = \\sum \\nu_i \\Delta_f H^\\circ(\\text{products}) - \\sum \\nu_j \\Delta_f H^\\circ(\\text{reactants})$.',
          },
        },
        {
          type: 'example',
          number: 2,
          problem: {
            english: '$\\Delta_f H^\\circ$ values (kJ/mol): CO₂ = $-393.5$, H₂O(l) = $-285.8$, CH₄ = $-74.8$. Find $\\Delta_c H^\\circ$ of CH₄.',
        hindi: '$\\Delta_f H^\\circ$ मान (kJ/mol): CO₂ = $-393.5$, H₂O(l) = $-285.8$, CH₄ = $-74.8$। CH₄ का $\\Delta_c H^\\circ$ ज्ञात करें।',
          },
          solution: {
            english: `CH₄ + 2O₂ → CO₂ + 2H₂O.
$\\Delta H = (-393.5) + 2(-285.8) - (-74.8) - 0 = -890.3$ kJ/mol.`,
            hindi: 'CH₄ + 2O₂ → CO₂ + 2H₂O. $\\Delta H = (-393.5) + 2(-285.8) - (-74.8) - 0 = -890.3$ kJ/mol.',
          },
        },
      ],
    },
    {
      id: 'second-third-law',
      title: {
        english: 'Second & Third Laws — Entropy',
        hindi: 'द्वितीय और तृतीय नियम — एन्ट्रॉपी',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Entropy $S$',
        hindi: 'एन्ट्रॉपी $S$',
          },
          body: {
            english: 'A state function measuring randomness/disorder. $\\Delta S = q_{rev}/T$ (J/K). Boltzmann: $S = k_B \\ln W$ where $W$ = microstates.',
        hindi: 'यादृच्छिकता/अव्यवस्था को मापने वाला एक राज्य फ़ंक्शन। $\\Delta S = q_{rev}/T$ (J/K). बोल्ट्ज़मैन: $S = k_B \\ln W$ जहां $W$ = माइक्रोस्टेट्स।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Second law**: total entropy of universe always increases for spontaneous process. $\\Delta S_{\\text{univ}} = \\Delta S_{\\text{sys}} + \\Delta S_{\\text{surr}} > 0$.',
        hindi: '**दूसरा नियम**: सहज प्रक्रिया के लिए ब्रह्मांड की कुल एन्ट्रापी हमेशा बढ़ती है। $\\Delta S_{\\text{univ}} = \\Delta S_{\\text{sys}} + \\Delta S_{\\text{surr}} > 0$.',
            },
            {
              english: '$\\Delta S_{\\text{surr}} = -q_{\\text{sys}}/T$ (constant $T$, $P$).',
        hindi: '$\\Delta S_{\\text{surr}} = -q_{\\text{sys}}/T$ (स्थिर $T$, $P$)।',
            },
            {
              english: 'Entropy increases: solid→liquid→gas; mixing; rise in $T$; increase in moles of gas.',
        hindi: 'एन्ट्रॉपी बढ़ती है: ठोस→तरल→गैस; मिश्रण; $T$ में वृद्धि; गैस के मोल में वृद्धि.',
            },
            {
              english: 'For phase change at $T_{\\text{trans}}$: $\\Delta S = \\Delta H/T_{\\text{trans}}$.',
        hindi: '$T_{\\text{trans}}$ पर चरण परिवर्तन के लिए: $\\Delta S = \\Delta H/T_{\\text{trans}}$।',
            },
            {
              english: 'For ideal gas isothermal: $\\Delta S = nR\\ln(V_2/V_1)$.',
        hindi: 'आदर्श गैस इज़ोटेर्मल के लिए: $\\Delta S = nR\\ln(V_2/V_1)$।',
            },
          ],
        },
        {
          type: 'paragraph',
          text: {
            english: '**Third law**: entropy of a perfectly crystalline pure substance at $T = 0$ K is zero. Allows absolute entropies $S^\\circ$ to be tabulated.',
        hindi: '**तीसरा नियम**: $T = 0$ K पर पूर्णतः क्रिस्टलीय शुद्ध पदार्थ की एन्ट्रापी शून्य होती है। पूर्ण एन्ट्रॉपी $S^\\circ$ को सारणीबद्ध करने की अनुमति देता है।',
          },
        },
      ],
    },
    {
      id: 'gibbs',
      title: {
        english: 'Gibbs Free Energy & Spontaneity',
        hindi: 'गिब्स मुक्त ऊर्जा और स्वतःस्फूर्तता',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Gibbs free energy',
        hindi: 'गिब्स मुक्त ऊर्जा',
          },
          statement: {
            english: '$G = H - TS$. At constant $T, P$: $\\Delta G = \\Delta H - T\\Delta S$. Spontaneity: $\\Delta G < 0$ spontaneous; $\\Delta G = 0$ equilibrium; $\\Delta G > 0$ non-spontaneous.',
        hindi: '$G = H - TS$. निरंतर $T, P$: $\\Delta G = \\Delta H - T\\Delta S$ पर। सहजता: $\\Delta G < 0$ सहज; $\\Delta G = 0$ संतुलन; $\\Delta G > 0$ गैर-सहज.',
          },
        },
        {
          type: 'table',
          headers: ['$\\Delta H$', '$\\Delta S$', 'Spontaneity'],
          rows: [
            ['$-$', '$+$', 'Spontaneous at all $T$'],
            ['$+$', '$-$', 'Non-spontaneous at all $T$'],
            ['$-$', '$-$', 'Spontaneous at low $T$ ($T < \\Delta H/\\Delta S$)'],
            ['$+$', '$+$', 'Spontaneous at high $T$ ($T > \\Delta H/\\Delta S$)'],
          ],
        },
        {
          type: 'list',
          items: [
            {
              english: 'Standard Gibbs energy and equilibrium: $\\Delta G^\\circ = -RT \\ln K$.',
        hindi: 'मानक गिब्स ऊर्जा और संतुलन: $\\Delta G^\\circ = -RT \\ln K$।',
            },
            {
              english: 'Useful: $\\Delta G^\\circ$ (kJ/mol) $= -2.303 RT \\log K$ (R = 8.314 J/mol·K, T in K).',
        hindi: 'उपयोगी: $\\Delta G^\\circ$ (kJ/mol) $= -2.303 RT \\log K$ (R = 8.314 J/mol·K, K में T)।',
            },
            {
              english: 'Maximum useful work obtainable from a process at constant $T, P$: $w_{\\text{max}} = \\Delta G$ (excluding PV work).',
        hindi: 'निरंतर $T, P$: $w_{\\text{max}} = \\Delta G$ (PV कार्य को छोड़कर) पर एक प्रक्रिया से प्राप्त होने वाला अधिकतम उपयोगी कार्य।',
            },
            {
              english: 'For electrochemical cell: $\\Delta G^\\circ = -nFE^\\circ_{\\text{cell}}$.',
        hindi: 'इलेक्ट्रोकेमिकल सेल के लिए: $\\Delta G^\\circ = -nFE^\\circ_{\\text{cell}}$।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\Delta U = q + w',
      desc: {
        english: 'First law',
        hindi: 'पहला कानून',
      },
    },
    {
      latex: 'w = -P_{\\text{ext}}\\Delta V',
      desc: {
        english: 'PV work',
        hindi: 'पीवी कार्य',
      },
    },
    {
      latex: 'w_{rev} = -nRT\\ln(V_2/V_1)',
      desc: {
        english: 'Isothermal reversible',
        hindi: 'इज़ोटेर्मल प्रतिवर्ती',
      },
    },
    {
      latex: '\\Delta H = \\Delta U + \\Delta n_g RT',
      desc: {
        english: 'Enthalpy change (gas)',
        hindi: 'एन्थैल्पी परिवर्तन (गैस)',
      },
    },
    {
      latex: 'C_P - C_V = R',
      desc: {
        english: 'Mayer\'s relation',
        hindi: 'मेयर का संबंध',
      },
    },
    {
      latex: '\\Delta G = \\Delta H - T\\Delta S',
      desc: {
        english: 'Gibbs free energy',
        hindi: 'गिब्स मुक्त ऊर्जा',
      },
    },
    {
      latex: '\\Delta G^\\circ = -RT \\ln K = -nFE^\\circ',
      desc: {
        english: 'G–K and G–E',
        hindi: 'जी-के और जी-ई',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'First law',
      problem: {
        english: '$\\Delta U = -100$ J, $q = -50$ J. Work?',
        hindi: '$\\Delta U = -100$ J, $q = -50$ J. काम?',
      },
      answer: {
        english: '$w = -50$ J (system does 50 J on surroundings)',
        hindi: '$w = -50$ J (सिस्टम परिवेश पर 50 J करता है)',
      },
      solution: {
        english: '$w = \\Delta U - q = -100 - (-50) = -50$.',
        hindi: '$w = \\Delta U - q = -100 - (-50) = -50$.',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Hess',
      problem: {
        english: 'Given $\\Delta_f H^\\circ$ (CO₂) = $-393$, (H₂O) = $-286$, (C₂H₆) = $-85$ kJ/mol. $\\Delta_c H^\\circ$ of C₂H₆?',
        hindi: 'दिया गया है $\\Delta_f H^\\circ$ (CO₂) = $-393$, (H₂O) = $-286$, (C₂H₆) = $-85$ kJ/mol. C₂H₆ का $\\Delta_c H^\\circ$?',
      },
      answer: {
        english: '$-1559$ kJ/mol',
        hindi: '$-1559$ kJ/mol C₂H₆ + kJ/mol O₂ → 2CO₂ + 3H₂O. $-1559$ .',
      },
      solution: {
        english: 'C₂H₆ + 7/2 O₂ → 2CO₂ + 3H₂O. $\\Delta H = 2(-393) + 3(-286) - (-85) = -1559$ kJ/mol.',
        hindi: 'C₂H₆ + 7/2 O₂ → 2CO₂ + 3H₂O. $\\Delta H = 2(-393) + 3(-286) - (-85) = -1559$ kJ/mol.',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Spontaneity',
      problem: {
        english: 'For $\\Delta H > 0$, $\\Delta S > 0$, reaction is spontaneous when:',
        hindi: '$\\Delta H > 0$, $\\Delta S > 0$ के लिए, प्रतिक्रिया स्वतःस्फूर्त होती है जब:',
      },
      answer: {
        english: '$T > \\Delta H/\\Delta S$',
        hindi: '$T > \\Delta H/\\Delta S$',
      },
      solution: {
        english: 'Set $\\Delta G = \\Delta H - T\\Delta S < 0$.',
        hindi: '$\\Delta G = \\Delta H - T\\Delta S < 0$ सेट करें.',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Entropy',
      problem: {
        english: 'For melting of ice at 0 °C ($\\Delta H_{\\text{fus}} = 6.0$ kJ/mol), $\\Delta S$?',
        hindi: '0 डिग्री सेल्सियस ($\\Delta H_{\\text{fus}} = 6.0$ kJ/mol), $\\Delta S$ पर बर्फ पिघलाने के लिए?',
      },
      answer: {
        english: '$22$ J/(mol·K)',
        hindi: '$22$ J/(mol·K)',
      },
      solution: {
        english: '$\\Delta S = \\Delta H/T = 6000/273$.',
        hindi: '$\\Delta S = \\Delta H/T = 6000/273$.',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'G ↔ K',
      problem: {
        english: 'For a reaction, $\\Delta G^\\circ = -45.7$ kJ/mol at 300 K. Equilibrium constant $K$?',
        hindi: 'एक प्रतिक्रिया के लिए, 300 K पर $\\Delta G^\\circ = -45.7$ kJ/mol। संतुलन स्थिरांक $K$?',
      },
      answer: {
        english: '$\\approx 10^8$',
        hindi: '$\\approx 10^8$',
      },
      solution: {
        english: '$\\log K = -\\Delta G^\\circ/(2.303 RT) = 45700/(2.303 \\cdot 8.314 \\cdot 300) \\approx 7.96 \\Rightarrow K \\approx 10^8$.',
        hindi: '$\\log K = -\\Delta G^\\circ/(2.303 RT) = 45700/(2.303 \\cdot 8.314 \\cdot 300) \\approx 7.96 \\Rightarrow K \\approx 10^8$.',
      },
    },
  ],
  next: {
    slug: 'equilibrium',
    title: 'Equilibrium',
  },
};

export default chapter;
